import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import getAddress from "../../utils/getAddress";
import LoadingWheel from "../shared/LoadingWheel";
import { IAccount } from "../../models/account/IAccount";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useAccountAddresses from "../../data/useAccountAddresses";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccountAddress } from "./../../models/account/IAccountAddress";
import { AccountAddressCreateEditModal } from "./AccountAddressCreateEditModal";
import { AccountAddressModal } from "./AccountAddressModal";

interface AccountAddressListRowProps {
  address: IAccountAddress;
  isBilling: boolean;
  isDelivery: boolean;
  onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
  onEditClick: (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onDeleteClick: (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
}

function AccountAddressListRow({
  address,
  isBilling = false,
  isDelivery = false,
  onClick,
  onEditClick,
  onDeleteClick,
}: AccountAddressListRowProps) {
  const { t } = useTranslation("accounts");

  return (
    <tr onClick={onClick} className="hover:cursor-pointer hover:bg-gray-100">
      <td className="px-3 py-3 border hidden lg:table-cell text-sm">
        {address.name}
      </td>
      <td className="px-3 py-3 border lg:table-cell text-sm">
        {getAddress(address)}
      </td>
      <td className="px-3 py-3 border hidden lg:table-cell text-sm text-center space-x-2">
        {isBilling && (
          <span className="px-3 py-1 rounded-full font-bold bg-blue-100 text-blue-500">
            {t("Billing")}
          </span>
        )}
        {isDelivery && (
          <span className="px-3 py-1 rounded-full font-bold bg-blue-100 text-blue-500">
            {t("Delivery")}
          </span>
        )}
      </td>
      <td className="px-3 py-3 border lg:table-cell text-sm">
        <div className="px-3 flex items-center space-x-4">
          <PencilIcon
            onClick={onEditClick}
            className="w-5 h-5 text-gray-600 hover:cursor-pointer"
          />
          <TrashIcon
            onClick={onDeleteClick}
            className="w-5 h-5 text-gray-600 hover:cursor-pointer"
          />
        </div>
      </td>
    </tr>
  );
}

export default function AccountAddressList({ account }: { account: IAccount }) {
  const { t } = useTranslation("accounts");
  const { accountAddresses, remove: deleteAccountAddress } =
    useAccountAddresses(account.id, "AccountAddressList");
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Address"));
  const [selectedAddress, setSelectedAddress] = useState<IAccountAddress>();
  const [viewOpen, setViewOpen] = useState(false);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  let editClearTimeout: NodeJS.Timeout;

  const handleClose = () => {
    if (selectedAddress) {
      editClearTimeout = setTimeout(() => {
        setSelectedAddress(undefined);
      }, 200);
    }

    setAddEditOpen(false);
  };

  const handleDelete = () => {
    if (!selectedAddress) return;

    deleteAccountAddress.mutate(
      { addressId: selectedAddress.id },
      {
        ...deleteHandlers,
        onSuccess: () => {
          deleteHandlers.onSuccess();
          setSelectedAddress(undefined);
          setDeleteOpen(false);
        },
      }
    );
  };

  useEffect(() => {
    return () => {
      if (editClearTimeout) {
        clearTimeout(editClearTimeout);
      }
    };
  }, []);

  if (accountAddresses.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (
    accountAddresses.isError ||
    !accountAddresses.data ||
    !accountAddresses.data.data
  ) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-red-600 font-bold text-xl">
          {t("Error loading addresses")}
        </p>
      </div>
    );
  } else {
    return (
      <>
        <AccountAddressModal
          address={selectedAddress}
          open={viewOpen}
          setOpen={setViewOpen}
        />
        <AccountAddressCreateEditModal
          account={account}
          address={selectedAddress}
          open={addEditOpen}
          setOpen={handleClose}
        />
        <ConfirmModal
          title={t("Delete Address")}
          message={t("Are you sure you want to delete this address? \nThis action cannot be undone.")}
          confirmButtonText={t("Delete")}
          onConfirm={handleDelete}
          isLoading={false}
          open={deleteOpen}
          setOpen={setDeleteOpen}
        />
        <div>
          <div className="p-2 sm:p-4 flex justify-end">
            <ButtonInput
              label={t("Add Address")}
              onClick={() => setAddEditOpen(true)}
              isSubmit={false}
              classes=""
            />
          </div>
          <div className="sm:hidden p-2">
            <p className="text-center">{t("Tap a row to see the full record")}</p>
          </div>
          <table className="min-w-full divide-y border-t border-collapse divide-gray-300">
            <thead>
              <tr className="bg-primary-50 text-gray-800">
                <th className="py-3.5 px-3 text-sm font-semibold hidden lg:table-cell text-left">
                  {t("Name")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                  {t("Address")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold hidden lg:table-cell text-left">
                  {t("Assignment")}
                </th>
                <th className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                  {t("Actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {accountAddresses.data.data.map((address) => (
                <AccountAddressListRow
                  key={address.id}
                  address={address}
                  isBilling={address.id === account.billingAddressId}
                  isDelivery={address.id === account.shippingAddressId}
                  onClick={() => {
                    setSelectedAddress(address);
                    setViewOpen(true);
                  }}
                  onEditClick={(e) => {
                    e.stopPropagation();
                    setSelectedAddress(address);
                    setAddEditOpen(true);
                  }}
                  onDeleteClick={(e) => {
                    e.stopPropagation();
                    setSelectedAddress(address);
                    setDeleteOpen(true);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }
}
